import { useLiteralListQueryParamState } from "hooks/useLiteralListQueryParamState";
import { useTenant } from "hooks/settings";
import { GroupedConsumptionSeries } from "../types";
import { Fetching } from "components/common/loading/fetching";
import { ExcelDownloadButton } from "components/common/ExcelDownloadButton";
import { useDateRange } from "contexts/DateRangeContext";
import { backendApi, downloadAsExcel } from "src/store/api/enhancedApi";
import { useEffect, useState } from "react";
import {
  ConsumptionSourceName,
  MaterialGroupingName,
  MaterialGroupName,
  TimeAggregation,
} from "src/store/api/generatedApi";
import { MultiSelect } from "../../common/inputs/multiSelect";
import { SelectBox } from "../Selectbox";
import { TableHeader } from "../TableHeader";
import { ToggleSelector } from "../ToggleSelector";
import { ConsumptionPlotType, DataLabelVisibility } from "../types";
import { ConsumptionPlot } from "./ConsumptionPlot";
import { ConsumptionTable } from "./ConsumptionTable";
import { DateRangeSelector } from "components/common/DateRangeSelector";
import { useConsumptionTabTimeline } from "./ConsumptionTabTimelineContext";
import { Grid, Stack } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { useStringQueryParamState } from "hooks/useStringQueryParamState";
import { typeSafeObjectKeys } from "src/utils/typeSafeObjectKeys";
import { defaultMaterialGrouping } from "../materialGroupings";

export const ConsumptionTabTimeline = () => {
  const tenant = useTenant();
  const { t } = useTenantTranslation();
  const {
    data: groupedConsumptionSeries,
    selectedTimeAggregation,
    setSelectedTimeAggregation,
    isFetching,
  } = useConsumptionTabTimeline();

  const [selectedProductGroup, setSelectedProductGroup] =
    useStringQueryParamState(
      "productGroup",
      getDefaultProductGroup(groupedConsumptionSeries.availableProductGroups)
    );

  const [selectedMaterialGrouping, setSelectedMaterialGrouping] =
    useStringQueryParamState(
      "materialGrouping",
      defaultMaterialGrouping(
        groupedConsumptionSeries.availableMaterialGroupings
      )
    );

  const [selectedPlotType, setSelectedPlotType] =
    useStringQueryParamState<ConsumptionPlotType>("plotType", "mass");

  const [dataLabelVisibility, setDataLabelVisibility] =
    useStringQueryParamState<DataLabelVisibility>("dataLabels", "hidden");

  const [selectedConsumptionSources, setSelectedConsumptionSources] =
    useLiteralListQueryParamState<ConsumptionSourceName>({
      paramName: "consumptionSources",
      permitted: groupedConsumptionSeries.availableConsumptionSources,
      localStorageKey: `${tenant}.strategyDashboard.consumption.selectedConsumptionSources`,
      defaultValue: groupedConsumptionSeries.availableConsumptionSources,
    });

  const materialGroupsInSelectedGrouping = selectedMaterialGrouping
    ? (groupedConsumptionSeries.availableMaterialGroupings[
        selectedMaterialGrouping
      ] ?? [])
    : [];

  const [selectedMaterialGroups, setSelectedMaterialGroups] =
    useLiteralListQueryParamState<MaterialGroupName>({
      paramName: "materialGroups",
      permitted: materialGroupsInSelectedGrouping,
      localStorageKey: `${tenant}.strategyDashboard.consumption.selectedMaterialGroups`,
      defaultValue: materialGroupsInSelectedGrouping,
    });

  const visibleMaterialGroups =
    selectedMaterialGroups.length > 0
      ? selectedMaterialGroups
      : materialGroupsInSelectedGrouping;

  const [selectedDate, setSelectedDate] = useState(() =>
    getDefaultSelectedDate(
      groupedConsumptionSeries,
      selectedMaterialGrouping,
      selectedProductGroup
    )
  );

  useEffect(() => {
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- resets date on filter change; user also clicks plot
    setSelectedDate(
      getDefaultSelectedDate(
        groupedConsumptionSeries,
        selectedMaterialGrouping,
        selectedProductGroup
      )
    );
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- use .updatedAt as stable proxy; full object causes extra date resets
  }, [
    selectedTimeAggregation,
    selectedMaterialGrouping,
    selectedProductGroup,
    groupedConsumptionSeries.updatedAt,
  ]);

  return (
    <Fetching fetching={isFetching}>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        sx={{ mt: 2 }}
      >
        <DateRangeSelector />
        <ConsumptionExcelButton
          timeAggregation={selectedTimeAggregation ?? "daily"}
          productGrouping={
            selectedProductGroup === "all" ? "all" : "productGroups"
          }
        />
      </Stack>
      <div>
        <Grid
          container
          spacing={2}
          sx={{ display: "flex", justifyContent: "flex-start", mt: 1 }}
        >
          <Grid>
            <ToggleSelector<TimeAggregation>
              title="frequency"
              selectedValue={selectedTimeAggregation}
              setSelectedValue={setSelectedTimeAggregation}
              availableValueLabels={{ daily: "daily", monthly: "monthly" }}
            />
          </Grid>
          <Grid>
            <SelectBox
              availableOptions={typeSafeObjectKeys(
                groupedConsumptionSeries.availableMaterialGroupings
              )}
              selectedOption={selectedMaterialGrouping}
              onOptionChange={setSelectedMaterialGrouping}
              id="material-grouping-select"
              labelId="material-grouping-label"
              label={t("materialGrouping")}
            />
          </Grid>
          <Grid>
            <SelectBox
              availableOptions={groupedConsumptionSeries.availableProductGroups}
              selectedOption={selectedProductGroup}
              onOptionChange={setSelectedProductGroup}
              id="product-group-select"
              labelId="product-group-label"
              label={t("productGroup")}
            />
          </Grid>
          <Grid>
            <ToggleSelector<ConsumptionPlotType>
              title="plotType"
              selectedValue={selectedPlotType}
              setSelectedValue={setSelectedPlotType}
              availableValueLabels={{ mass: "mass", fraction: "fraction" }}
            />
          </Grid>
          <Grid>
            <ToggleSelector<DataLabelVisibility>
              title="dataLabels"
              selectedValue={dataLabelVisibility}
              setSelectedValue={setDataLabelVisibility}
              availableValueLabels={{ shown: "show", hidden: "hide" }}
            />
          </Grid>
          <Grid>
            <MultiSelect<ConsumptionSourceName, ConsumptionSourceName>
              sx={{ minWidth: 240 }}
              options={Array.from(
                groupedConsumptionSeries.availableConsumptionSources
              )}
              getOptionId={(source) => source}
              getOptionLabel={(source) => t(source)}
              values={selectedConsumptionSources}
              setValues={setSelectedConsumptionSources}
              small
              selectAll
              labelAbove
              label={t("consumptionSources")}
            />
          </Grid>
          <Grid>
            <MultiSelect<MaterialGroupName, MaterialGroupName>
              sx={{ minWidth: 240 }}
              options={materialGroupsInSelectedGrouping}
              getOptionId={(materialGroup) => materialGroup}
              getOptionLabel={(materialGroup) => t(materialGroup)}
              values={selectedMaterialGroups}
              setValues={setSelectedMaterialGroups}
              small
              selectAll
              labelAbove
              label={t("materialGroups")}
            />
          </Grid>
        </Grid>
        <Grid container spacing={1} mt={2} mx={0}>
          <Grid ml={0} size={{ xs: 7 }}>
            <ConsumptionPlot
              consumptionSeries={groupedConsumptionSeries}
              selectedGrouping={selectedMaterialGrouping}
              selectedProductGroup={selectedProductGroup}
              selectedPlotType={selectedPlotType}
              selectedConsumptionSources={selectedConsumptionSources}
              selectedMaterialGroups={visibleMaterialGroups}
              dataLabelVisibility={dataLabelVisibility}
              setSelectedDate={setSelectedDate}
            />
          </Grid>
          <Grid size={{ xs: 5 }}>
            <TableHeader
              titlePrefix={t("consumptionDuring")}
              subtitleText={t("consumptionTableSubtitleText")}
              selectedDate={selectedDate}
              formatDateMonthly={selectedTimeAggregation === "monthly"}
            />
            <ConsumptionTable
              consumptionSeries={groupedConsumptionSeries}
              selectedDate={selectedDate}
              selectedMaterialGrouping={selectedMaterialGrouping}
              selectedProductGroup={selectedProductGroup}
              selectedConsumptionSources={selectedConsumptionSources}
            />
          </Grid>
        </Grid>
      </div>
    </Fetching>
  );
};

const ConsumptionExcelButton = ({
  timeAggregation,
  productGrouping,
}: {
  timeAggregation: TimeAggregation;
  productGrouping: string;
}) => {
  const { startDate, endDate } = useDateRange();
  const tenantName = useTenant();
  const [trigger] =
    backendApi.endpoints.getStrategyDashboardConsumption.useLazyQuery();
  const start = startDate.format();
  const end = endDate.format();
  const getBlob = () =>
    downloadAsExcel(trigger, {
      tenantName,
      start,
      end,
      timeAggregation,
      productGrouping,
      "Cache-Control": "no-cache",
    });
  return <ExcelDownloadButton variant="outlined" getBlob={getBlob} />;
};

const getDefaultProductGroup = (availableProductGroups: string[]) => {
  return availableProductGroups.includes("all")
    ? "all"
    : (Array.from(availableProductGroups)[0] ?? null);
};

const getDefaultSelectedDate = (
  groupedConsumptionSeries: GroupedConsumptionSeries,
  selectedMaterialGrouping: MaterialGroupingName | null,
  selectedProductGroup: string | null
): Date | null => {
  const defaultSelectedDateString = groupedConsumptionSeries.items
    .filter(
      (item) =>
        item[0].materialGrouping == selectedMaterialGrouping &&
        (selectedProductGroup === "all"
          ? item[0].productGrouping === "all" // When "all" is selected, only show aggregated data
          : item[0].productGroup === selectedProductGroup) // When specific group is selected, show that group's data
    )
    .map((item) => item[1].date[item[1].date.length - 1] ?? null)
    .reduce((max, current) => {
      if (max === null) {
        return current;
      }
      if (current === null) {
        return max;
      }
      return current > max ? current : max;
    }, null);
  return defaultSelectedDateString
    ? new Date(Date.parse(defaultSelectedDateString))
    : null;
};
